import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend,
} from 'recharts';
import {
  C,
  WS_GLOBAL_STYLE,
  parseCSV,
  inferTypes,
  colStats,
  getHistogram,
  getValueCounts,
  pearson,
  PinContext,
} from './labs/wsShared';
import DataTable from './labs/DataTable';
import CleaningLab from './labs/CleaningLab';
import DistributionLab from './labs/DistributionLab';
import CorrelationLab from './labs/CorrelationLab';
import StatisticsLab from './labs/StatisticsLab';
import MLLab from './labs/MLLab';
import { startUsageSession, usageStep, usageWorkflowFromTabs } from '../utils/usageTracker';
import PublishExperimentModal from './ui/PublishExperimentModal';
import HFDatasetPicker from './HFDatasetPicker';
import { idbSave, idbLoad, validateRowHeaders } from './data/idbStore';
import { getWorkspaceActionSummary } from './data/workspaceVersioning';
import { workspace as workspaceApi } from '../api/client';

const ML_TABS = (ds) => [
  { id: 'data', label: 'DATA SOURCE', icon: '📁', color: C.cyan },
  { id: 'profile', label: 'PROFILE', icon: '◈', color: C.cyan, disabled: !ds },
  { id: 'clean', label: 'CLEANING', icon: '◉', color: C.orange, disabled: !ds },
  { id: 'eda', label: 'DATA ANALYSIS', icon: '⬡', color: C.violet, disabled: !ds },
  { id: 'feature_eng', label: 'FEATURE ENGINEERING', icon: '⬟', color: C.green, disabled: !ds },
  { id: 'ml_train', label: 'TRAIN', icon: '⬢', color: C.orange, disabled: !ds },
  { id: 'ml_compare', label: 'COMPARE', icon: '⬡', color: C.violet, disabled: !ds },
  { id: 'ml_tune', label: 'TUNE', icon: '◎', color: C.gold, disabled: !ds },
  { id: 'report', label: 'REPORT', icon: '📄', color: C.green },
];

const ML_USAGE_WORKFLOW = usageWorkflowFromTabs([
  { id: 'upload', label: 'UPLOAD' },
  { id: 'profile', label: 'PROFILE' },
  { id: 'clean', label: 'CLEANING' },
  { id: 'eda', label: 'DATA ANALYSIS' },
  { id: 'feature_eng', label: 'FEATURES' },
  { id: 'ml_train', label: 'TRAIN' },
  { id: 'ml_compare', label: 'COMPARE' },
  { id: 'ml_tune', label: 'TUNE' },
  { id: 'report', label: 'REPORT' },
]);

const SAMPLE_DATASETS = [
  { name: 'Iris.csv', url: 'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/iris.csv', desc: '150 rows · 5 cols · classic classification' },
  { name: 'Titanic.csv', url: 'https://raw.githubusercontent.com/datasciencedojo/datasets/master/titanic.csv', desc: '891 rows · 12 cols · survival analysis' },
  { name: 'Tips.csv', url: 'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/tips.csv', desc: '244 rows · 7 cols · regression practice' },
  { name: 'Diamonds.csv', url: 'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/diamonds.csv', desc: '53940 rows · 10 cols · price prediction' },
  { name: 'Penguins.csv', url: 'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/penguins.csv', desc: '344 rows · 7 cols · multi-class classif.' },
  { name: 'MPGE.csv', url: 'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/mpg.csv', desc: '398 rows · 9 cols · fuel efficiency' },
];

function fmtBytes(b) {
  if (!b) return '—';
  if (b >= 1_048_576) return (b / 1_048_576).toFixed(1) + ' MB';
  if (b >= 1024) return Math.round(b / 1024) + ' KB';
  return b + ' B';
}

function profileDataset(ds) {
  const { rows, headers } = ds;
  if (!rows?.length || !headers?.length) return null;

  const types = inferTypes(headers, rows);
  const numCols = types.filter((t) => t.type === 'numeric').map((t) => t.col);
  const catCols = types.filter((t) => t.type === 'categorical').map((t) => t.col);

  const nullMap = {};
  headers.forEach((header) => {
    nullMap[header] = rows.filter((row) => row[header] === '' || row[header] == null).length;
  });
  const totalNulls = Object.values(nullMap).reduce((sum, count) => sum + count, 0);
  const nullPct = totalNulls > 0 ? +((totalNulls / (rows.length * headers.length)) * 100).toFixed(2) : 0;

  const rowKeys = rows.map((row) => headers.map((header) => row[header]).join('\x00'));
  const dupCount = rowKeys.length - new Set(rowKeys).size;

  const colProfiles = headers.map((header) => {
    const t = types.find((typeInfo) => typeInfo.col === header);
    const nullN = nullMap[header];
    const nullP = +((nullN / rows.length) * 100).toFixed(1);
    const stats = t?.type === 'numeric' ? colStats(rows, header) : null;
    const valueCounts = t?.type === 'categorical' ? getValueCounts(rows, header, 8) : null;
    const hist = t?.type === 'numeric' ? getHistogram(rows, header, 16) : null;

    const isId = t?.type === 'numeric' && t.uniqueCount / rows.length > 0.92 && rows.length > 30;
    const isConst = t?.uniqueCount <= 1;
    const isHighCard = t?.type === 'categorical' && t.uniqueCount > rows.length * 0.5 && rows.length > 50;
    const isBinary = t?.uniqueCount === 2;
    const flags = [
      isConst && { label: 'CONSTANT', color: C.red },
      isId && { label: 'LIKELY ID', color: '#fbbf24' },
      isHighCard && { label: 'HIGH CARD', color: '#fbbf24' },
      isBinary && { label: 'BINARY', color: C.cyan },
      nullP > 50 && { label: `${nullP}% NULL`, color: C.red },
      nullP > 10 && nullP <= 50 && { label: `${nullP}% NULL`, color: '#fbbf24' },
    ].filter(Boolean);

    let shape = null;
    if (stats) {
      const values = rows.map((row) => parseFloat(row[header])).filter((value) => !isNaN(value));
      const rawKurt = values.length > 3
        ? values.reduce((sum, value) => sum + Math.pow((value - stats.mean) / Math.max(stats.std, 1e-9), 4), 0) / values.length - 3
        : 0;
      shape = {
        skewLabel: Math.abs(stats.skew) < 0.5 ? 'Normal' : stats.skew > 0 ? 'Right-skew' : 'Left-skew',
        kurtosis: +rawKurt.toFixed(3),
        outlierPct: +((stats.outlierCount / Math.max(stats.count, 1)) * 100).toFixed(1),
        cv: stats.mean !== 0 ? +(Math.abs(stats.std / stats.mean) * 100).toFixed(1) : null,
      };
    }

    return {
      col: header,
      type: t?.type || '?',
      uniqueCount: t?.uniqueCount || 0,
      nullCount: nullN,
      nullPct: nullP,
      stats,
      valueCounts,
      hist,
      shape,
      flags,
    };
  });

  const corrCols = numCols.slice(0, 12);
  const corrSample = rows.slice(0, 3000);
  const topCorr = [];
  for (let i = 0; i < corrCols.length; i += 1) {
    for (let j = i + 1; j < corrCols.length; j += 1) {
      const r = pearson(corrSample, corrCols[i], corrCols[j]);
      topCorr.push({ a: corrCols[i], b: corrCols[j], r, abs: Math.abs(r) });
    }
  }
  topCorr.sort((a, b) => b.abs - a.abs);

  const qualityScore = Math.max(0, Math.round(
    100
    - nullPct * 1.2
    - (dupCount / Math.max(rows.length, 1)) * 25
    - colProfiles.filter((p) => p.flags.some((f) => f.label === 'CONSTANT')).length * 5
    - colProfiles.filter((p) => p.nullPct > 30).length * 3
    - (numCols.length === 0 ? 10 : 0)
  ));

  return {
    types,
    numCols,
    catCols,
    nullMap,
    nullPct,
    dupCount,
    totalNulls,
    colProfiles,
    topCorr,
    qualityScore,
    meta: {
      rows: rows.length,
      cols: headers.length,
      numeric: numCols.length,
      categorical: catCols.length,
      completeRows: rows.filter((row) => headers.every((header) => row[header] && row[header] !== '')).length,
    },
  };
}

const Pill = ({ label, color = C.cyan, size = 8 }) => (
  <span style={{ fontSize: size, padding: '2px 6px', background: `${color}18`, border: `1px solid ${color}40`, borderRadius: 2, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</span>
);

const MetCard = ({ label, value, color = C.cyan, sub, large }) => (
  <div style={{ padding: '12px 14px', background: C.raised, border: `1px solid ${C.b1}`, borderLeft: `2px solid ${color}`, borderRadius: 3 }}>
    <div style={{ fontSize: 7.5, color: C.t4, letterSpacing: '.12em', fontFamily: 'JetBrains Mono,monospace', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: large ? 26 : 20, color, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>{sub}</div>}
  </div>
);

const CTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.b2}`, padding: '8px 12px', borderRadius: 4, fontSize: 11 }}>
      <div style={{ color: C.t3, marginBottom: 3, fontFamily: 'JetBrains Mono,monospace', fontSize: 9 }}>{label}</div>
      {payload.map((point, index) => (
        <div key={index} style={{ color: point.color || C.cyan }}>{point.name}: <b>{typeof point.value === 'number' ? point.value.toFixed(4) : point.value}</b></div>
      ))}
    </div>
  );
};

function QualityRing({ score, size = 80 }) {
  const color = score >= 80 ? C.green : score >= 60 ? '#fbbf24' : C.red;
  const r = size * 0.37;
  const cx = size / 2;
  const cy = size / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={`${color}22`} strokeWidth={size * 0.1} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={size * 0.1} strokeDasharray={`${c * (score / 100)} ${c * (1 - score / 100)}`} strokeDashoffset={c * 0.25} strokeLinecap="round" style={{ transition: 'stroke-dasharray .6s ease' }} />
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill={color} fontSize={size * 0.21} fontWeight="900" fontFamily="Syne,sans-serif">{score}</text>
    </svg>
  );
}

const SectionHead = ({ label, color = C.cyan }) => (
  <div style={{ fontSize: 9, color, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.13em', fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ height: 1, width: 18, background: color, opacity: 0.5 }} />
    {label}
    <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${color}44,transparent)` }} />
  </div>
);

function ProfileTab({ ds, profile }) {
  const { meta, qualityScore, colProfiles, nullMap, nullPct, dupCount, topCorr } = profile;
  const [view, setView] = useState('schema');

  const nullData = ds.headers
    .map((header) => ({ col: header.length > 16 ? `${header.slice(0, 14)}…` : header, full: header, nulls: nullMap[header], pct: +((nullMap[header] / ds.rows.length) * 100).toFixed(1) }))
    .sort((a, b) => b.nulls - a.nulls)
    .filter((d) => d.nulls > 0);

  const typeChartData = [
    { name: 'Numeric', value: meta.numeric, fill: C.cyan },
    { name: 'Categorical', value: meta.categorical, fill: '#fbbf24' },
  ].filter((d) => d.value > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(6,1fr)', gap: 10, alignItems: 'stretch' }}>
        <div style={{ padding: '10px 14px', background: C.raised, border: `1px solid ${C.b1}`, borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          <QualityRing score={qualityScore} size={72} />
          <div style={{ fontSize: 7.5, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em' }}>QUALITY</div>
        </div>
        <MetCard label="Rows" value={meta.rows.toLocaleString()} color={C.cyan} />
        <MetCard label="Columns" value={meta.cols} color={C.green} />
        <MetCard label="Numeric Cols" value={meta.numeric} color={C.violet} />
        <MetCard label="Cat Cols" value={meta.categorical} color="#fbbf24" />
        <MetCard label="Null Cells" value={`${nullPct}%`} color={nullPct > 20 ? C.red : nullPct > 5 ? '#fbbf24' : C.green} sub={`${profile.totalNulls.toLocaleString()} cells`} />
        <MetCard label="Duplicates" value={dupCount.toLocaleString()} color={dupCount > 0 ? '#fbbf24' : C.green} />
      </div>

      <div style={{ display: 'flex', gap: 2, background: C.deep, border: `1px solid ${C.b0}`, borderRadius: 4, padding: 3, flexWrap: 'wrap' }}>
        {[
          ['schema', 'COLUMN SCHEMA'],
          ['nullmap', 'NULL MAP'],
          ['sample', 'DATA PREVIEW'],
          ['correlations', 'CORRELATIONS'],
        ].map(([id, lbl]) => (
          <button key={id} onClick={() => setView(id)} style={{ padding: '6px 14px', border: 'none', borderRadius: 3, cursor: 'pointer', background: view === id ? C.cyan : 'transparent', color: view === id ? C.void : C.t2, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, letterSpacing: '.09em' }}>{lbl}</button>
        ))}
      </div>

      {view === 'schema' && (
        <div>
          <SectionHead label="FULL COLUMN PROFILE" />
          <div style={{ overflowX: 'auto', border: `1px solid ${C.b1}`, borderRadius: 4 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, minWidth: 900 }}>
              <thead>
                <tr style={{ background: C.deep }}>
                  {['#', 'Column', 'Type', 'Unique', 'Nulls', 'Null%', 'Min', 'Max', 'Mean', 'Std Dev', 'Skew', 'Outliers%', 'Flags'].map((h) => (
                    <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.07em', fontWeight: 700, borderBottom: `1px solid ${C.b1}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {colProfiles.map((p, i) => (
                  <tr key={p.col} style={{ borderBottom: `1px solid ${C.b0}`, background: i % 2 ? `${C.raised}60` : 'transparent' }}>
                    <td style={{ padding: '6px 10px', color: C.t4, fontSize: 9, fontFamily: 'JetBrains Mono,monospace' }}>{i}</td>
                    <td style={{ padding: '6px 10px', color: C.t1, fontWeight: 600, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.col}</td>
                    <td style={{ padding: '6px 10px' }}><Pill label={p.type === 'numeric' ? 'NUM' : 'CAT'} color={p.type === 'numeric' ? C.cyan : '#fbbf24'} /></td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: C.t2 }}>{p.uniqueCount.toLocaleString()}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: p.nullCount > 0 ? C.red : C.t4 }}>{p.nullCount.toLocaleString()}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: p.nullPct > 20 ? C.red : p.nullPct > 5 ? '#fbbf24' : C.t4 }}>{p.nullPct}%</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: C.t3, fontSize: 9 }}>{p.stats?.min ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: C.t3, fontSize: 9 }}>{p.stats?.max ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: C.t2, fontSize: 9 }}>{p.stats?.mean ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: C.t2, fontSize: 9 }}>{p.stats?.std ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: Math.abs(p.stats?.skew || 0) > 1 ? '#fbbf24' : C.t3, fontSize: 9 }}>{p.stats?.skew ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'JetBrains Mono,monospace', color: p.shape?.outlierPct > 5 ? C.red : C.t3, fontSize: 9 }}>{p.shape?.outlierPct ?? '—'}</td>
                    <td style={{ padding: '6px 10px' }}>
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {p.flags.length === 0
                          ? <Pill label="✓ CLEAN" color={C.green} />
                          : p.flags.map((f, j) => <Pill key={j} label={f.label} color={f.color} />)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'nullmap' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
          <div>
            <SectionHead label="NULL COUNT PER COLUMN" />
            {nullData.length > 0 ? (
              <div style={{ height: Math.min(320, nullData.length * 26 + 60) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={nullData} layout="vertical" margin={{ top: 4, right: 14, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.b1} horizontal={false} />
                    <XAxis type="number" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={(v) => `${v}`} />
                    <YAxis type="category" dataKey="col" width={110} tick={{ fill: C.t2, fontSize: 9, fontFamily: 'JetBrains Mono,monospace' }} />
                    <Tooltip content={<CTip />} formatter={(v, n, p) => [`${v} rows (${p.payload.pct}%)`, 'Null count']} />
                    <Bar dataKey="nulls" radius={[0, 3, 3, 0]}>
                      {nullData.map((d, i) => <Cell key={i} fill={d.pct > 20 ? C.red : d.pct > 5 ? '#fbbf24' : C.green} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ padding: 30, textAlign: 'center', background: C.raised, borderRadius: 4, border: `1px solid ${C.green}30` }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
                <div style={{ color: C.green, fontSize: 12, fontWeight: 600 }}>No null values in dataset</div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SectionHead label="SUMMARY" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                ['Total null cells', `${profile.totalNulls.toLocaleString()} / ${(ds.rows.length * ds.headers.length).toLocaleString()}`, nullPct > 10 ? C.red : '#fbbf24'],
                ['Null %', `${nullPct}%`, nullPct > 20 ? C.red : nullPct > 5 ? '#fbbf24' : C.green],
                ['Duplicate rows', dupCount.toLocaleString(), dupCount > 0 ? '#fbbf24' : C.green],
                ['Complete rows', `${((meta.completeRows / ds.rows.length) * 100).toFixed(1)}%`, C.cyan],
                ['Cols with any null', nullData.length.toString(), nullData.length > 0 ? '#fbbf24' : C.green],
                ['Cols >50% null', colProfiles.filter((p) => p.nullPct > 50).length.toString(), C.red],
              ].map(([lbl, val, color]) => (
                <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: C.raised, borderRadius: 3 }}>
                  <span style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{lbl}</span>
                  <span style={{ fontSize: 10, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{val}</span>
                </div>
              ))}
            </div>
            <div style={{ height: 160, marginTop: 4 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={typeChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="35%" outerRadius="65%" paddingAngle={3}>
                    {typeChartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip content={<CTip />} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 9 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {view === 'sample' && <DataTable rows={ds.rows} headers={ds.headers} types={profile.types} showControls />}

      {view === 'correlations' && (
        <div>
          <SectionHead label="TOP PEARSON CORRELATIONS" />
          {topCorr.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {topCorr.slice(0, 15).map((p, i) => {
                const col = Math.abs(p.r) > 0.7 ? (p.r > 0 ? C.green : C.red) : Math.abs(p.r) > 0.4 ? '#fbbf24' : C.t3;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: C.raised, borderRadius: 3, border: `1px solid ${C.b1}` }}>
                    <span style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', minWidth: 14 }}>#{i + 1}</span>
                    <span style={{ fontSize: 10, color: C.t1, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.a}</span>
                    <span style={{ fontSize: 9, color: C.t4 }}>↔</span>
                    <span style={{ fontSize: 10, color: C.t1, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.b}</span>
                    <div style={{ width: 80, height: 5, background: C.b1, borderRadius: 3, flexShrink: 0 }}>
                      <div style={{ width: `${Math.abs(p.r) * 100}%`, height: '100%', background: col, borderRadius: 3 }} />
                    </div>
                    <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, fontWeight: 700, color: col, minWidth: 50, textAlign: 'right' }}>r={p.r.toFixed(3)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: 30, textAlign: 'center', color: C.t4 }}>Need at least 2 numeric columns for correlation analysis.</div>
          )}
        </div>
      )}
    </div>
  );
}

function DataUpload({ onLoaded, autoUrl, autoCsv, onAutoConsumed }) {
  const [src, setSrc] = useState('file');
  const [showHF, setShowHF] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDrag, setIsDrag] = useState(false);
  const [urlVal, setUrlVal] = useState('');
  const [loadMsg, setLoadMsg] = useState('');
  const fileRef = useRef();

  const ingest = useCallback(async (text, filename, sizeBytes = 0) => {
    try {
      await new Promise(r => setTimeout(r, 0)); // yield — let browser paint spinner
      const parsed = parseCSV(text);
      if (!parsed || !parsed.rows.length) {
        setError('Parse failed — check CSV format');
        setLoading(false);
        return;
      }
      setLoadMsg(`${parsed.rows.length.toLocaleString()} rows · ${parsed.headers.length} cols${sizeBytes ? ' · ' + fmtBytes(sizeBytes) : ''}`);
      onLoaded({ name: filename, headers: parsed.headers, rows: parsed.rows });
      setLoading(false);
    } catch (eventError) {
      setError(`Error: ${eventError.message}`);
      setLoading(false);
    }
  }, [onLoaded]);

  const processFile = useCallback((file) => {
    if (!file) return;
    if (!/\.(csv|tsv|txt)$/i.test(file.name)) {
      setError('Only CSV / TSV files');
      return;
    }
    setLoading(true);
    setError('');
    const reader = new FileReader();
    reader.onload = (event) => ingest(file.name.endsWith('.tsv') ? String(event.target.result).replace(/\t/g, ',') : String(event.target.result), file.name, file.size);
    reader.readAsText(file);
  }, [ingest]);

  const fetchURL = useCallback(async (url, name) => {
    if (!String(url || '').trim()) {
      setError('Enter a URL');
      return;
    }
    const filename = name || (String(url).split('/').pop() || 'dataset.csv');
    setLoading(true);
    setError('');
    try {
      const response = await fetch(String(url).trim());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      ingest(text, filename, text.length);
    } catch (eventError) {
      setError(`Fetch failed: ${eventError.message}`);
      setLoading(false);
    }
  }, [ingest]);

  useEffect(() => {
    if (!autoUrl || loading) return;
    setSrc('url');
    setUrlVal(String(autoUrl));
    fetchURL(String(autoUrl));
    onAutoConsumed?.();
  }, [autoUrl, loading, fetchURL, onAutoConsumed]);

  useEffect(() => {
    if (!autoCsv?.csvText || loading) return;
    setSrc('file');
    ingest(String(autoCsv.csvText), autoCsv.name || 'dataset.csv');
    onAutoConsumed?.();
  }, [autoCsv?.id, autoCsv?.csvText, autoCsv?.name, loading, ingest, onAutoConsumed]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 40px 32px', minHeight: '100%', position: 'relative' }}>
      <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 300, background: `radial-gradient(ellipse,${C.orange}08 0%,transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ textAlign: 'center', marginBottom: 40, position: 'relative' }}>
        <h1 style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 38, color: C.t1, lineHeight: 1.1, margin: '0 0 12px' }}>
          Train, Test, Compare
          <br />
          <span style={{ background: `linear-gradient(90deg,${C.orange},${C.cyan})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            
            ML Experiments in Minutes
          </span>
        </h1>
        <p style={{ fontSize: 12, color: C.t3, maxWidth: 700, lineHeight: 1.85, margin: '0 auto' }}>
          Classification · Regression · Clustering · Model Comparison · Feature Importance · Reports
          <br></br>No setup required, Just mouse clicks. 
        </p>

        <span style={{ display:'inline-block', marginTop:6, padding:'4px 10px', borderRadius:999, background:`linear-gradient(90deg, ${C.cyan}18, ${C.violet}16)`, border:`1px solid ${C.cyan}33`, color:C.t1 }}>
                    Your data never leaves your machine. 100% on Browser & 100% Privacy.
                  </span>
         
      </div>

      
      <div style={{ width: '100%', maxWidth: 720, background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 8, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,.6)', position: 'relative', marginBottom: 28 }}>
        <div style={{ display: 'flex', background: C.deep, borderBottom: `1px solid ${C.b0}` }}>
          {[['file', '📁 Upload File'], ['hf', '🤗 HuggingFace'], ['url', '🌐 From URL'], ['sample', '⬟ Sample Datasets']].map(([id, label]) => (
            <button key={id} onClick={() => { if (id === 'hf') { setShowHF(true); return; } setSrc(id); setError(''); }} style={{ flex: 1, padding: '14px', border: 'none', background: (id === 'hf' ? showHF : src === id) ? C.surface : 'transparent', color: (id === 'hf' ? showHF : src === id) ? C.cyan : C.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, cursor: 'pointer', borderBottom: `2px solid ${(id === 'hf' ? showHF : src === id) ? C.cyan : 'transparent'}`, transition: 'all .12s' }}>{label}</button>
          ))}
        </div>
        <div style={{ padding: 30 }}>
          {src === 'file' && (
            <div
              onDragOver={(event) => { event.preventDefault(); setIsDrag(true); }}
              onDragLeave={() => setIsDrag(false)}
              onDrop={(event) => { event.preventDefault(); setIsDrag(false); processFile(event.dataTransfer.files[0]); }}
              onClick={() => fileRef.current?.click()}
              style={{ border: `2px dashed ${isDrag ? C.cyan : C.b2}`, borderRadius: 8, padding: '60px 20px', textAlign: 'center', cursor: loading ? 'wait' : 'pointer', background: isDrag ? `${C.cyan}07` : C.deep, transition: 'all .2s' }}
            >
              <div style={{ fontSize: 52, marginBottom: 12, opacity: 0.5 }}>{loading ? '⏳' : '📊'}</div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 700, fontSize: 17, color: C.t1, marginBottom: 6 }}>{loading ? 'Parsing...' : 'Drop your CSV here'}</div>
              <div style={{ fontSize: 11, color: C.t3 }}>{loading ? (loadMsg || 'Building full analysis…') : 'or click to browse · CSV / TSV / TXT'}</div>
              <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" style={{ display: 'none' }} onChange={(event) => processFile(event.target.files[0])} />
            </div>
          )}
          {src === 'url' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>PUBLIC CSV / TSV URL</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={urlVal} onChange={(event) => setUrlVal(event.target.value)} placeholder="https://example.com/data.csv" onKeyDown={(event) => event.key === 'Enter' && fetchURL(urlVal)} style={{ flex: 1, background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '12px 14px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 4, outline: 'none' }} />
                <button onClick={() => fetchURL(urlVal)} disabled={loading} style={{ padding: '12px 22px', background: `${C.cyan}18`, border: `1px solid ${C.cyan}`, color: C.cyan, fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 700, borderRadius: 4, cursor: 'pointer' }}>{loading ? '…' : 'FETCH'}</button>
              </div>
            </div>
          )}
          {src === 'sample' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
              {SAMPLE_DATASETS.map((sample) => (
                <button key={sample.name} onClick={() => fetchURL(sample.url, sample.name)} disabled={loading} style={{ padding: '16px', background: C.deep, border: `1px solid ${C.b1}`, borderRadius: 6, cursor: 'pointer', textAlign: 'left', transition: 'border-color .15s', opacity: loading ? 0.6 : 1 }} onMouseEnter={(event) => { event.currentTarget.style.borderColor = C.cyan; }} onMouseLeave={(event) => { event.currentTarget.style.borderColor = C.b1; }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>⬟</div>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, fontSize: 11, color: C.t1, marginBottom: 3 }}>{sample.name}</div>
                  <div style={{ fontSize: 8.5, color: C.t4, lineHeight: 1.5 }}>{sample.desc}</div>
                </button>
              ))}
            </div>
          )}
          {showHF && (
            <HFDatasetPicker
              accentColor={C.cyan}
              onClose={() => setShowHF(false)}
              onLoaded={(csv, name, size) => ingest(csv, name, size)}
            />
          )}

          {error && <div style={{ marginTop: 12, padding: '9px 14px', background: `${C.red}10`, border: `1px solid ${C.red}40`, borderRadius: 4, fontSize: 10, color: C.red }}>{error}</div>}
        </div>
      </div>
    </div>
  );
}

function MLExperimentDetail({ experiment, onUpdate, pendingData, onPendingConsumed, onFreshDataLoaded, snapshotOnly, onSnapshotDataRestored }) {
  const tabs = useMemo(() => ML_TABS(experiment.ds), [experiment.ds]);
  const profile = useMemo(() => (experiment.ds ? profileDataset(experiment.ds) : null), [experiment.ds]);
  const [edaSub, setEdaSub] = useState('distribution');
  const displayTabs = useMemo(
    () => tabs.map((tab) => {
      if (tab.id === 'data') return { ...tab, label: 'UPLOAD', icon: '⬆' };
      return tab;
    }),
    [tabs]
  );

  const defaultTab = experiment.ds
    ? (displayTabs.find((t) => t.id === 'profile')?.id ?? 'data')
    : 'data';

  const [tab, setTab] = useState(experiment.activeTab || defaultTab);
  const [dashboardCards, setDashboardCards] = useState(experiment.dashboardCards || []);
  const experimentRef = useRef(experiment);
  useEffect(() => { experimentRef.current = experiment; }, [experiment]);
  const mlSessionKey = `${experiment.id}:${experiment.dataSessionId || 0}`;

  useEffect(() => {
    const desiredTab = displayTabs.some((t) => t.id === experiment.activeTab && !t.disabled)
      ? experiment.activeTab
      : defaultTab;
    if (desiredTab) {
      setTab((prev) => (desiredTab !== prev ? desiredTab : prev));
    }
  }, [experiment.id, experiment.activeTab]);

  useEffect(() => {
    if (!displayTabs.some((t) => t.id === tab)) setTab(defaultTab);
  }, [displayTabs, tab, defaultTab]);

  useEffect(() => {
    setDashboardCards(experiment.dashboardCards || []);
  }, [experiment.dashboardCards, experiment.dataSessionId]);

  useEffect(() => {
    if (!experiment.ds) return;
    if (!ML_USAGE_WORKFLOW.includes(tab) || tab === 'upload') return;
    usageStep('ml_experiments', tab, { experiment_id: experiment.id, tab }, ML_USAGE_WORKFLOW);
  }, [experiment.ds, experiment.id, tab]);

  const update = useCallback((patch) => {
    const current = experimentRef.current;
    onUpdate({ ...current, ...patch });
  }, [onUpdate]);

  const handleTabChange = useCallback((nextTab) => {
    setTab(nextTab);
    if (experimentRef.current?.activeTab !== nextTab) update({ activeTab: nextTab });
  }, [update]);

  useEffect(() => {
    if (dashboardCards !== experimentRef.current?.dashboardCards) {
      onUpdate((prev) => ({ ...prev, dashboardCards }));
    }
  }, [dashboardCards, onUpdate]);

  const pinChart = useCallback((chart) => {
    setDashboardCards((prev) => {
      const exists = prev.some((item) => item?.chart?.id === chart?.id);
      if (exists) return prev.filter((item) => item?.chart?.id !== chart?.id);
      return [...prev, { chart, pinnedAt: Date.now() }];
    });
  }, []);

  const pinnedIds = useMemo(() => new Set(dashboardCards.map((item) => item.chart.id)), [dashboardCards]);

  const handleDataLoaded = useCallback((ds) => {
    window.dispatchEvent(new CustomEvent('dd-collapse-left'));
    startUsageSession('ml_experiments');
    usageStep('ml_experiments', 'upload', {
      experiment_id: experiment.id,
      rows: ds?.rows?.length || 0,
      cols: ds?.headers?.length || 0,
    }, ML_USAGE_WORKFLOW);
    if (snapshotOnly) {
      // Restoring raw rows into a saved snapshot workspace — keep all ML results and workspace link.
      onSnapshotDataRestored?.(ds);
      setTab('profile');
      return;
    }
    const nextSessionId = Date.now();
    onFreshDataLoaded?.();
    setDashboardCards([]);
    update({
      ds,
      status: 'active',
      dataSource: ds?.name || 'uploaded',
      mlConfig: null,
      mlResults: null,
      dashboardCards: [],
      dataSessionId: nextSessionId,
      activeTab: 'profile',
    });
    setTab('profile');
  }, [experiment.id, snapshotOnly, onFreshDataLoaded, onSnapshotDataRestored, update]);

  const handleCleanDs = useCallback((newDs) => update({ ds: newDs }), [update]);

  const renderTab = () => {
    if (tab === 'data') {
      return (
        <DataUpload
          onLoaded={handleDataLoaded}
          autoUrl={pendingData?.type === 'url' ? pendingData.url : null}
          autoCsv={pendingData?.type === 'csv' ? pendingData : null}
          onAutoConsumed={onPendingConsumed}
        />
      );
    }
    if (!experiment.ds && tab !== 'report') return null;
    if (tab === 'profile') return profile ? <ProfileTab ds={experiment.ds} profile={profile} /> : null;
    if (tab === 'clean') return <CleaningLab ds={experiment.ds} onUpdate={handleCleanDs} />;
    if (tab === 'eda') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              { id: 'distribution', label: 'DISTRIBUTIONS', color: C.green },
              { id: 'correlation', label: 'CORRELATIONS', color: C.violet },
              { id: 'statistics', label: 'STATS', color: C.cyan },
            ].map((subTab) => (
              <button
                key={subTab.id}
                onClick={() => setEdaSub(subTab.id)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 2,
                  border: `1px solid ${edaSub === subTab.id ? subTab.color : C.b1}`,
                  background: edaSub === subTab.id ? `${subTab.color}18` : 'transparent',
                  color: edaSub === subTab.id ? subTab.color : C.t3,
                  cursor: 'pointer',
                  fontSize: 9,
                  fontFamily: 'JetBrains Mono,monospace',
                  fontWeight: 700,
                  letterSpacing: '.08em',
                }}
              >
                {subTab.label}
              </button>
            ))}
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 6, padding: 12 }}>
            {edaSub === 'distribution' && <DistributionLab ds={experiment.ds} />}
            {edaSub === 'correlation' && <CorrelationLab ds={experiment.ds} />}
            {edaSub === 'statistics' && <StatisticsLab ds={experiment.ds} />}
          </div>
        </div>
      );
    }
    if (tab === 'feature_eng') return <MLLab key={`${mlSessionKey}:features`} ds={experiment.ds} subTab="features" experiment={experiment} onUpdate={update} dashboardCards={dashboardCards} />;
    if (tab === 'ml_train') return <MLLab key={`${mlSessionKey}:train`} ds={experiment.ds} subTab="train" experiment={experiment} onUpdate={update} dashboardCards={dashboardCards} />;
    if (tab === 'ml_compare') return <MLLab key={`${mlSessionKey}:compare`} ds={experiment.ds} subTab="compare" experiment={experiment} onUpdate={update} dashboardCards={dashboardCards} />;
    if (tab === 'ml_tune') return <MLLab key={`${mlSessionKey}:tune`} ds={experiment.ds} subTab="tune" experiment={experiment} onUpdate={update} dashboardCards={dashboardCards} />;
    if (tab === 'report') return <MLLab key={`${mlSessionKey}:report`} ds={experiment.ds} subTab="report" experiment={experiment} onUpdate={update} dashboardCards={dashboardCards} />;
    return null;
  };

  return (
    <PinContext.Provider value={{ pinChart, pinnedIds }}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div style={{ background: C.deep, borderBottom: `1px solid ${C.b0}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', padding: '4px 6px', gap: 2 }}>
            {displayTabs.map((tabItem) => {
              const active = tabItem.id === tab;
              return (
                <button
                  key={tabItem.id}
                  onClick={() => !tabItem.disabled && handleTabChange(tabItem.id)}
                  disabled={tabItem.disabled}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '8px 11px', border: 'none', background: 'transparent',
                    color: active ? (tabItem.color || C.cyan) : (tabItem.disabled ? C.t4 : C.t3),
                    borderBottom: `2px solid ${active ? (tabItem.color || C.cyan) : 'transparent'}`,
                    fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: active ? 700 : 400,
                    cursor: tabItem.disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', letterSpacing: '.08em',
                    opacity: tabItem.disabled ? 0.5 : 1, transition: 'all .1s',
                  }}
                >
                  {tabItem.icon && <span style={{ fontSize: 12 }}>{tabItem.icon}</span>}
                  <span>{tabItem.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: tab === 'data' ? 0 : 18, minHeight: 0 }}>
          {renderTab()}
        </div>
      </div>
    </PinContext.Provider>
  );
}

// IDB session key for ML — module-level constant
const ML_IDB_KEY = 'ml_exp:local';

function defaultExperiment() {
  return {
    id: Date.now(),
    name: 'ML Experiment',
    moduleType: 'machine_learning',
    status: 'new',
    color: C.orange,
    createdAt: new Date().toISOString(),
    dataSessionId: 0,
    ds: null,
    notes: [],
    reportState: null,
    dashboardCards: [],
  };
}

export default function MLExperimentsHub({ isAuthenticated, authUser, workspaceRestore, onWorkspaceRestored }) {
  const [experiment, setExperiment] = useState(defaultExperiment);
  const [pendingData, setPendingData] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [checkpointing, setCheckpointing] = useState(false);
  const [workspaceId,   setWorkspaceId]   = useState(null);
  const [saveVersion,   setSaveVersion]   = useState(0);
  const [baseVersion,   setBaseVersion]   = useState(0);
  const [showSaveInfo,  setShowSaveInfo]  = useState(false);
  const [snapshotOnly,  setSnapshotOnly]  = useState(false);
  const resetWorkspaceTracking = useCallback(() => {
    setWorkspaceId(null);
    setSaveVersion(0);
    setBaseVersion(0);
    setSnapshotOnly(false);
  }, []);

  // Called when user re-uploads a file while viewing a snapshot-only workspace.
  // Merges fresh rows into the existing experiment without discarding any results or workspace link.
  const handleSnapshotDataRestored = useCallback((freshDs) => {
    setSnapshotOnly(false);
    setExperiment(prev => ({
      ...prev,
      ds: {
        ...(prev.ds || {}),
        rows: freshDs.rows,
        name: freshDs.name || prev.ds?.name,
        headers: freshDs.headers?.length ? freshDs.headers : (prev.ds?.headers || []),
      },
      dataSessionId: Date.now(),
      activeTab: 'profile',
    }));
  }, []);
  const persistLocal = useCallback((data) => {
    const wsId = data?.workspaceId;
    idbSave(wsId ? `ml_exp:${wsId}` : ML_IDB_KEY, data).catch(() => {});
  }, []);

  // IDB auto-restore removed: every left-panel click = fresh start.
  // Workspace resume (from homepage) is handled below.
  const idbRestoredRef = useRef(false);

  // Autosave to IDB whenever state changes (debounced 5 s)
  const mlAutosaveTimer = useRef(null);
  useEffect(() => {
    if (!experiment.ds) return;
    clearTimeout(mlAutosaveTimer.current);
    mlAutosaveTimer.current = setTimeout(() => {
      persistLocal({ experiment, workspaceId, saveVersion, baseVersion });
    }, 5_000);
    return () => clearTimeout(mlAutosaveTimer.current);
  }, [experiment, workspaceId, saveVersion, baseVersion, persistLocal]);

  const buildSnapshot = useCallback(() => {
    const ds = experiment.ds;
    if (!ds) return {};
    const mlResults = experiment.mlResults || {};
    const metrics = {};
    if (mlResults.bestModel) metrics.best_model = String(mlResults.bestModel);
    if (mlResults.accuracy != null) metrics.accuracy = String((mlResults.accuracy * 100).toFixed(1) + '%');
    if (mlResults.f1 != null) metrics.f1_score = String(mlResults.f1.toFixed(4));
    if (mlResults.task) metrics.task = String(mlResults.task);
    // Rich report data
    const richResults = Object.values(mlResults)
      .filter(r => r && typeof r === 'object' && r.algoId)
      .slice(0, 10)
      .map(r => ({
        algoId:   r.algoId,
        algoName: r.algoName,
        color:    r.color || null,
        taskType: r.taskType,
        metrics:  r.metrics  || {},
        confusion: r.confusion || null,
        nTrain:   r.nTrain,
        nTest:    r.nTest,
      }));
    const mlConfig = experiment.mlConfig || {};

    // Full-dataset profiles for precomputed EDA in View Report
    const prof = profileDataset(ds);
    const col_profiles = (prof?.colProfiles || []).slice(0, 50).map(p => ({
      col:         p.col,
      type:        p.type,
      uniqueCount: p.uniqueCount,
      nullPct:     p.nullPct,
      stats: p.stats ? {
        mean:   +Number(p.stats.mean).toFixed(4),
        std:    +Number(p.stats.std).toFixed(4),
        min:    +Number(p.stats.min).toFixed(4),
        max:    +Number(p.stats.max).toFixed(4),
        median: +Number(p.stats.median).toFixed(4),
        skew:   +Number(p.stats.skew).toFixed(4),
      } : null,
      valueCounts: p.type === 'categorical'
        ? getValueCounts(ds.rows, p.col, 30).map(vc => ({ v: vc.name, n: vc.count }))
        : [],
      hist: p.type === 'numeric'
        ? getHistogram(ds.rows, p.col, 20).map((b, i, arr) => ({
            x0: b.label,
            x1: arr[i + 1]?.label ?? b.label,
            n:  b.count,
          }))
        : [],
    }));
    const correlations = (prof?.topCorr || []).slice(0, 15).map(c => ({
      a: c.a, b: c.b, r: +Number(c.r).toFixed(4),
    }));
    const corr_scatter = (prof?.topCorr || []).slice(0, 6).map(c => {
      const allPts = ds.rows.map(r => {
        const x = parseFloat(r[c.a]), y = parseFloat(r[c.b]);
        return (!isNaN(x) && !isNaN(y)) ? [+x.toFixed(4), +y.toFixed(4)] : null;
      }).filter(Boolean);
      const step = allPts.length > 2000 ? Math.ceil(allPts.length / 2000) : 1;
      return { a: c.a, b: c.b, pts: allPts.filter((_, i) => i % step === 0).slice(0, 2000) };
    });
    const null_map = Object.fromEntries(Object.entries(prof?.nullMap || {}).slice(0, 50));

    // Feature importance on full dataset for View Report
    const profTypes = prof?.types || [];
    const { targetCol = '', featureCols = [] } = mlConfig;
    const ml_feature_importance = (targetCol && featureCols.length > 0)
      ? featureCols.slice(0, 20).map(col => {
          const ct = profTypes.find(t => t.col === col);
          const tt = profTypes.find(t => t.col === targetCol);
          let imp = 0;
          if (ct?.type === 'numeric' && tt?.type === 'numeric') {
            imp = Math.abs(pearson(ds.rows, col, targetCol));
          } else if (ct?.type === 'numeric') {
            const classes = [...new Set(ds.rows.map(r => r[targetCol]).filter(Boolean))];
            const allV = ds.rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v));
            const gm = allV.length ? allV.reduce((a, b) => a + b, 0) / allV.length : 0;
            const bv = classes.reduce((s, c) => {
              const gv = ds.rows.filter(r => r[targetCol] === c).map(r => parseFloat(r[col])).filter(v => !isNaN(v));
              return gv.length ? s + gv.length * (gv.reduce((a, b) => a + b, 0) / gv.length - gm) ** 2 : s;
            }, 0) / (allV.length || 1);
            const tv = allV.reduce((s, v) => s + (v - gm) ** 2, 0) / (allV.length || 1) || 1;
            imp = Math.min(1, bv / tv);
          }
          return { col, type: ct?.type, imp: +imp.toFixed(4) };
        }).sort((a, b) => b.imp - a.imp)
      : [];

    // ── Final-form fields: stored exactly as the Report tab uses them ────────
    // snap_types: [{col, type, uniqueCount}] — same shape as inferTypes output
    const snap_types = profTypes;

    // null_cols: [{h, n, pct}] — full-dataset null counts
    const snap_null_cols = Object.entries(prof?.nullMap || {})
      .filter(([, n]) => n > 0)
      .map(([col, n]) => ({ h: col, n, pct: +((n / ds.rows.length) * 100).toFixed(1) }));

    // corrState is NOT stored — reconstructed in ReportViewer from correlations+corr_scatter.
    // snap_dist_col_data is NOT stored — ReportViewer rebuilds distColData from col_profiles.

    return {
      dataset: {
        name: ds.name || '',
        rows: ds.rows?.length || 0,
        cols: ds.headers?.length || 0,
      },
      metrics,
      insights: [],
      tabs_completed: ['data', 'profile', 'clean', 'feature_eng', 'ml_train'],
      // Rich report data
      col_headers: (ds.headers || []).slice(0, 60),
      sample_rows: (ds.rows || []).slice(0, 50).map(r =>
        Array.isArray(r) ? r.slice(0, 60) : (ds.headers || []).slice(0, 60).map(h => r[h] ?? null)
      ),
      ml_results: richResults,
      ml_config: {
        targetCol:   mlConfig.targetCol   || '',
        featureCols: mlConfig.featureCols || [],
        encodings:   mlConfig.encodings   || {},
        scalingMethod: mlConfig.scalingMethod || {},
        // projections = all N rows × k components — can be hundreds of KB for large datasets.
        // Strip it from the snapshot; MLReportTab recomputes it from the 50 sample rows.
        pcaResult: mlConfig.pcaResult ? { ...mlConfig.pcaResult, projections: null } : null,
      },
      notes: (experiment.notes || []).slice(0, 20),
      // Legacy EDA fields (used by AutoEDA ReportComposer path)
      col_profiles,
      correlations,
      corr_scatter,
      null_map,
      quality_score:    prof?.qualityScore ?? 0,
      quality_insights: [],
      ml_feature_importance,
      // Final-form fields for ML View Report
      snap_types,
      snap_null_cols,
      report_state: experiment.reportState || null,
    };
    // Dev guard: warn if snapshot is close to the 512KB server limit
    if (process.env.NODE_ENV !== 'production') {
      const kb = Math.round(new Blob([JSON.stringify(snap)]).size / 1024);
      if (kb > 400) console.warn(`[buildSnapshot] snapshot is ${kb}KB — approaching 512KB server limit`);
      else console.log(`[buildSnapshot] snapshot size: ${kb}KB`);
    }
    return snap;
  }, [experiment]);

  // Handle workspace restore from WorkspaceDock
  useEffect(() => {
    if (!workspaceRestore || workspaceRestore.module_id !== 'ml_experiments') return;
    idbRestoredRef.current = true;
    const desiredTab = workspaceRestore.active_tab;
    const requestedVersion = Number(workspaceRestore.version_no || 0) || null;

    const fetchAndRestore = async () => {
      try {
        const [result, perWsLocal, flatLocal] = await Promise.all([
          requestedVersion && workspaceRestore.id
            ? workspaceApi.get(workspaceRestore.id, { version: requestedVersion })
            : workspaceApi.get(workspaceRestore.id),
          idbLoad(`ml_exp:${workspaceRestore.id}`),
          idbLoad(ML_IDB_KEY),
        ]);
        // Per-workspace key takes priority; flat key used for backward compat.
        const localSaved = perWsLocal || (flatLocal?.workspaceId === workspaceRestore.id ? flatLocal : null);
        const version = result?.workspace?.selected_version;
        const snap = version?.snapshot;
        const ws = result?.workspace;
        if (snap?.experiment) {
          const expectedHeaders = snap.experiment.ds?.headers || [];
          const rawLocalRows = localSaved?.experiment?.ds?.rows || [];
          const localRows = validateRowHeaders(rawLocalRows, expectedHeaders);
          const nextExperiment = {
            ...snap.experiment,
            activeTab: desiredTab || snap.experiment.activeTab || 'data',
            ds: snap.experiment.ds ? { ...snap.experiment.ds, rows: localRows } : null,
          };
          const nextVersion = version.version_no || requestedVersion || 1;
          setExperiment(nextExperiment);
          setWorkspaceId(workspaceRestore.id);
          setSaveVersion(nextVersion);
          setBaseVersion(nextVersion);
          setSnapshotOnly(localRows.length === 0);
          persistLocal({ experiment: nextExperiment, workspaceId: workspaceRestore.id, saveVersion: nextVersion, baseVersion: nextVersion });
        } else if (ws) {
          // Server snapshot is null (saved before size fix, or too large).
          // Tier 2: IDB on this browser may have the full experiment including rows.
          if (localSaved?.workspaceId === workspaceRestore.id && localSaved?.experiment?.ds) {
            const idbExp = localSaved.experiment;
            const nextExperiment = { ...idbExp, activeTab: desiredTab || idbExp.activeTab || 'data' };
            const nextVersion = localSaved.saveVersion || version?.version_no || 1;
            setExperiment(nextExperiment);
            setWorkspaceId(workspaceRestore.id);
            setSaveVersion(nextVersion);
            setBaseVersion(nextVersion);
            setSnapshotOnly(!(idbExp.ds.rows?.length > 0));
            persistLocal({ experiment: nextExperiment, workspaceId: workspaceRestore.id, saveVersion: nextVersion, baseVersion: nextVersion });
          } else {
            // Tier 3: nothing locally — show hub skeleton so user can re-upload without losing workspace link.
            const fallbackDs = { headers: [], name: ws.summary?.filename || ws.label || 'dataset.csv', rows: [] };
            const nextExperiment = { ...defaultExperiment(), activeTab: 'data', ds: fallbackDs };
            setExperiment(nextExperiment);
            setWorkspaceId(workspaceRestore.id);
            setSaveVersion(version?.version_no || 1);
            setBaseVersion(version?.version_no || 1);
            setSnapshotOnly(true);
          }
        }
      } catch {}
      onWorkspaceRestored?.();
    };
    fetchAndRestore();
  }, [workspaceRestore]);

  const doSave = useCallback(async () => {
    if (!experiment.ds || !isAuthenticated) return;
    setCheckpointing(true);
    try {
      const ds = experiment.ds;
      const mlResults = experiment.mlResults || {};
      const payload = {
        parent_version: baseVersion || saveVersion || undefined,
        active_tab: experiment.activeTab || 'data',
        summary: {
          filename: ds.name || '',
          rows: ds.rows?.length || 0,
          cols: ds.headers?.length || 0,
          best_model: mlResults.bestModel ? String(mlResults.bestModel) : undefined,
          best_score: mlResults.accuracy != null ? mlResults.accuracy : undefined,
          task_type: mlResults.task ? String(mlResults.task) : undefined,
        },
        meta: { tabs_visited: ['data', 'profile', 'clean', 'feature_eng', 'ml_train'] },
        actions: getWorkspaceActionSummary('ml_experiments', authUser),
      };
      let newId = workspaceId;
      let nextVersion = saveVersion;
      // Strip per-row prediction arrays (ypred/yte) so the snapshot stays under the 512 KB backend limit.
      const trimMlResults = (r) => r ? Object.fromEntries(Object.entries(r).map(([k, v]) => [
        k, (v && typeof v === 'object') ? (({ ypred, yte, ...rest }) => rest)(v) : v
      ])) : null;
      const makeExpSnap = () => ({ ...experiment, ds: experiment.ds ? { headers: experiment.ds.headers, name: experiment.ds.name } : null, mlResults: trimMlResults(experiment.mlResults) });
      if (!workspaceId) {
        const result = await workspaceApi.save({ module_id: 'ml_experiments', label: ds.name || 'ML Experiment', ...payload, snapshot: { experiment: makeExpSnap() } });
        newId = result.id;
        nextVersion = result.version_no || 1;
        setWorkspaceId(newId);
      } else {
        const result = await workspaceApi.update(workspaceId, { ...payload, snapshot: { experiment: makeExpSnap() } });
        nextVersion = result.version_no || (saveVersion + 1);
      }
      setSaveVersion(nextVersion);
      setBaseVersion(nextVersion);
      setSnapshotOnly(false);
      persistLocal({ experiment, workspaceId: newId, saveVersion: nextVersion, baseVersion: nextVersion });
      window.dispatchEvent(new CustomEvent('dd-workspace-saved'));
    } catch (e) {
      // silent — best-effort
    } finally {
      setCheckpointing(false);
    }
  }, [experiment, workspaceId, saveVersion, baseVersion, isAuthenticated, authUser, persistLocal]);

  const handleCheckpoint = useCallback(() => {
    if (saveVersion === 0) { setShowSaveInfo(true); } else { doSave(); }
  }, [saveVersion, doSave]);

  useEffect(() => {
    const onNew = (event) => {
      setPendingData(null);
      setExperiment(defaultExperiment());
      resetWorkspaceTracking();
    };
    const onReset = (event) => {
      setPendingData(null);
      setExperiment(defaultExperiment());
      resetWorkspaceTracking();
    };
    const onLoadUrl = (event) => {
      const url = event?.detail?.url;
      if (!url) return;
      const name = event?.detail?.name || (String(url).split('/').pop() || 'dataset.csv');
      const nextExperiment = defaultExperiment();
      setExperiment(nextExperiment);
      resetWorkspaceTracking();
      setPendingData({ id: Date.now(), type: 'url', url: String(url), name: String(name), targetExpId: nextExperiment.id });
    };
    const onLoadCsv = (event) => {
      const csvText = event?.detail?.csvText;
      if (!csvText) return;
      const name = event?.detail?.name || 'dataset.csv';
      const nextExperiment = defaultExperiment();
      setExperiment(nextExperiment);
      resetWorkspaceTracking();
      setPendingData({ id: Date.now(), type: 'csv', csvText: String(csvText), name: String(name), targetExpId: nextExperiment.id });
    };
    window.addEventListener('dd:expws:new', onNew);
    window.addEventListener('dd:expws:reset', onReset);
    window.addEventListener('dd:expws:load_url', onLoadUrl);
    window.addEventListener('dd:expws:load_csv', onLoadCsv);
    return () => {
      window.removeEventListener('dd:expws:new', onNew);
      window.removeEventListener('dd:expws:reset', onReset);
      window.removeEventListener('dd:expws:load_url', onLoadUrl);
      window.removeEventListener('dd:expws:load_csv', onLoadCsv);
    };
  }, [resetWorkspaceTracking]);

  if (workspaceRestore && workspaceRestore.module_id === 'ml_experiments') return (
    <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--void)' }}>
      <div style={{ fontFamily:'JetBrains Mono,monospace', fontSize:11, color:'var(--t3)', letterSpacing:'.06em' }}>⏳ RESTORING WORKSPACE…</div>
    </div>
  );

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: C.void }}>
      <style>{WS_GLOBAL_STYLE}</style>
      {snapshotOnly && (
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 14px', background:'#fbbf2412', borderBottom:'1px solid #fbbf2435', flexShrink:0, fontFamily:'JetBrains Mono,monospace', fontSize:10, color:'#fbbf24' }}>
          <span>&#9888;</span>
          <span>Raw data not in this browser — re-upload your dataset to reactivate experiments. All trained results and configurations are preserved.</span>
        </div>
      )}
      {experiment.ds && isAuthenticated && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 14px', background: 'var(--deep)', borderBottom: '1px solid var(--b0)', flexShrink: 0, gap: 8 }}>
          <button
            onClick={handleCheckpoint}
            disabled={checkpointing}
            style={{ padding: '5px 14px', background: `${C.green}18`, border: `1px solid ${C.green}50`, color: C.green, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 3, cursor: checkpointing ? 'wait' : 'pointer', letterSpacing: '.07em', fontWeight: 700, opacity: checkpointing ? 0.6 : 1 }}
          >
            {checkpointing ? '⏳ SAVING…' : saveVersion > 0 ? `💾 SAVE VERSION [${saveVersion}]` : '💾 SAVE VERSION'}
          </button>
          <button
            onClick={() => setPublishOpen(true)}
            style={{ padding: '5px 14px', background: `${C.orange}18`, border: `1px solid ${C.orange}50`, color: C.orange, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 3, cursor: 'pointer', letterSpacing: '.07em', fontWeight: 700 }}
          >
            📤 PUBLISH EXPERIMENT
          </button>
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0 }}>
        <MLExperimentDetail
          experiment={experiment}
          onUpdate={setExperiment}
          pendingData={pendingData && pendingData.targetExpId === experiment.id ? pendingData : null}
          onPendingConsumed={() => setPendingData(null)}
          onFreshDataLoaded={resetWorkspaceTracking}
          snapshotOnly={snapshotOnly}
          onSnapshotDataRestored={handleSnapshotDataRestored}
        />
      </div>
      {showSaveInfo && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#111', border:`1px solid ${C.green}40`, borderRadius:8, padding:'28px 32px', maxWidth:440, fontFamily:'JetBrains Mono,monospace' }}>
            <div style={{ color:C.green, fontSize:13, fontWeight:700, marginBottom:12 }}>💾 SAVE VERSION</div>
            <div style={{ color:'#ccc', fontSize:11, lineHeight:1.8, marginBottom:20 }}>
              Raw uploaded data stays <strong style={{ color:'#fff' }}>in this browser</strong>.{' '}
              Saved versions store a lightweight experiment snapshot and workflow history on the server.{' '}
              On another device, you may still need to re-upload the same dataset to retrain or compare models.
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowSaveInfo(false)} style={{ padding:'6px 14px', background:'transparent', border:'1px solid #444', color:'#888', fontFamily:'JetBrains Mono,monospace', fontSize:9, borderRadius:3, cursor:'pointer' }}>
                CANCEL
              </button>
              <button onClick={() => { setShowSaveInfo(false); doSave(); }} style={{ padding:'6px 14px', background:`${C.green}20`, border:`1px solid ${C.green}60`, color:C.green, fontFamily:'JetBrains Mono,monospace', fontSize:9, borderRadius:3, cursor:'pointer', fontWeight:700 }}>
                GOT IT · SAVE
              </button>
            </div>
          </div>
        </div>
      )}
      {publishOpen && (
        <PublishExperimentModal
          snapshot={buildSnapshot()}
          expType="ml_experiments"
          onClose={() => setPublishOpen(false)}
          onPublished={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
}
